/**
 * /admin/aprobaciones-rutas — Pantalla para autorizadores con permiso
 * `rutas:autorizar_salida_incompleta`.
 * Lista las solicitudes pendientes y permite aprobar/rechazar con observación.
 */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldAlert, CheckCircle2, XCircle, RefreshCw, Loader2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AprobacionesRutas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolveId, setResolveId] = useState(null);
  const [aprobar, setAprobar] = useState(true);
  const [observ, setObserv] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/rutas-overrides-pendientes");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando solicitudes");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // refresco 20s
    return () => clearInterval(t);
  }, []);

  const openResolve = (id, ap) => {
    setResolveId(id);
    setAprobar(ap);
    setObserv("");
  };

  const resolver = async () => {
    if (!resolveId) return;
    setSubmitting(true);
    try {
      await api.post(`/rutas/${resolveId}/carga/resolver-override`, {
        aprobar,
        observaciones: observ || null,
      });
      toast.success(aprobar ? "Aprobada · el montador puede salir" : "Rechazada");
      setResolveId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="aprobaciones-rutas-page">
      <div className="flex items-center justify-between">
        <PageHeader title="Aprobaciones de rutas" subtitle="Solicitudes para salir con bultos incompletos" />
        <Button onClick={load} size="sm" variant="outline" data-testid="refresh-btn">
          <RefreshCw className={"w-3.5 h-3.5 mr-2 " + (loading ? "animate-spin" : "")} /> Refrescar
        </Button>
      </div>

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-emerald-900/40 bg-emerald-950/10 p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
          <h3 className="text-emerald-200 font-semibold">Sin solicitudes pendientes</h3>
          <p className="text-emerald-300/70 text-sm">No hay rutas esperando autorización ahora mismo.</p>
        </div>
      )}

      {items.map((r) => {
        const ov = r.carga_override || {};
        return (
          <div key={r.id} className="rounded-xl border border-amber-500/30 bg-amber-950/10 p-4"
            data-testid={`solicitud-${r.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <ShieldAlert className="w-5 h-5 text-amber-400 mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/rutas/${r.id}`} className="text-zinc-100 font-semibold hover:text-amber-300">
                    Ruta {r.codigo}
                  </Link>
                  <Badge variant="outline" className="text-xs">{r.fecha}</Badge>
                  <Badge className="bg-red-500/20 text-red-300 border border-red-500/40 text-xs">
                    {ov.faltantes_count} bultos faltan
                  </Badge>
                </div>
                <div className="text-xs text-zinc-400 mt-1">{r.nombre}</div>
                <div className="text-sm text-zinc-300 mt-3 p-3 rounded bg-zinc-900/60 border border-zinc-800">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Motivo:</div>
                  {ov.motivo}
                </div>
                <div className="text-[10px] text-zinc-500 mt-2">
                  Solicitado por <b className="text-zinc-300">{ov.solicitado_por}</b> · {fmt(ov.solicitado_at)}
                </div>
              </div>
            </div>

            {/* Lista de componentes faltantes */}
            {(ov.faltantes_snapshot || []).length > 0 && (
              <div className="text-xs text-zinc-400 mb-3 pl-8">
                <div className="uppercase tracking-wider text-zinc-500 mb-1">Componentes faltantes:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {(ov.faltantes_snapshot || []).slice(0, 8).map((f, i) => (
                    <li key={i} className="text-zinc-300">
                      <span className="font-mono text-amber-300 text-[10px]">[{String(f.componente_id).slice(0, 6).toUpperCase()}]</span> {f.nombre}
                    </li>
                  ))}
                  {(ov.faltantes_snapshot || []).length > 8 && (
                    <li className="text-zinc-500 italic">…y {ov.faltantes_snapshot.length - 8} más</li>
                  )}
                </ul>
              </div>
            )}

            <div className="flex gap-2 pl-8">
              <Button onClick={() => openResolve(r.id, true)}
                className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
                data-testid={`aprobar-${r.id}`}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
              </Button>
              <Button onClick={() => openResolve(r.id, false)}
                variant="outline"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                data-testid={`rechazar-${r.id}`}>
                <XCircle className="w-4 h-4 mr-1" /> Rechazar
              </Button>
            </div>
          </div>
        );
      })}

      {/* Dialog de resolución */}
      <Dialog open={!!resolveId} onOpenChange={(o) => !o && setResolveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {aprobar ? "Aprobar salida con bultos incompletos" : "Rechazar solicitud"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-300">
              {aprobar
                ? "El montador podrá arrancar la ruta inmediatamente. Tu nombre quedará registrado como autorizador."
                : "El montador NO podrá arrancar la ruta hasta completar la carga."}
            </p>
            <div>
              <label className="text-xs text-zinc-400">Observaciones (opcional)</label>
              <textarea
                value={observ}
                onChange={(e) => setObserv(e.target.value)}
                placeholder={aprobar ? "Ej: el bulto X lo llevarás mañana directamente al cliente." : "Ej: completar primero."}
                rows={3}
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm"
                data-testid="observaciones-resolver"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveId(null)}>Cancelar</Button>
            <Button onClick={resolver} disabled={submitting}
              className={aprobar
                ? "bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
                : "bg-red-500 text-white hover:bg-red-400"}
              data-testid="confirmar-resolver-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> :
                (aprobar ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />)}
              {aprobar ? "Aprobar" : "Rechazar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
